// /components/attractionSectionClient.tsx
"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { LazyMotion, domAnimation, m } from "framer-motion";
import type { CarouselApi } from "@relume_io/relume-ui";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@relume_io/relume-ui";

import TextLinkButton from "@/components/ui/TextLinkButton";
import Underline from "@/components/ui/underline";
import CtaButton from "@/components/ui/CtaButton";

type Card = {
    key: string;
    index: number;
    title: string;
    description: string;
    imageSrc: string | null;
    imageAlt: string;
    button: {
        label: string;
        href: string;
    };
};

type Props = {
    tagline?: string;
    heading: string;
    description: string;
    allLink?: { label: string; href: string };
    cards: Card[];
    className?: string;
};

const slideUp = {
    hidden: { y: 40, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

async function preloadAndDecodeAll(urls: string[]) {
    if (typeof window === "undefined") return;

    const uniq = Array.from(new Set(urls)).slice(0, 6);

    await Promise.all(
        uniq.map(async (src) => {
            await new Promise<void>((resolve) => {
                const img = new window.Image();
                img.decoding = "async";
                img.onload = () => resolve();
                img.onerror = () => resolve(); // nie blokuj
                img.src = src;
            });

            // decode() pomaga na iOS/Safari, żeby obraz był “gotowy” do renderu
            try {
                const img2 = new window.Image();
                img2.decoding = "async";
                img2.src = src;
                if (img2.decode) await img2.decode();
            } catch {
                // ignore
            }
        })
    );
}

export function AttractionSectionClient({
                                            tagline = "ATRAKCJE",
                                            heading,
                                            description,
                                            allLink,
                                            cards,
                                            className,
                                        }: Props) {
    const [api, setApi] = React.useState<CarouselApi>();
    const [current, setCurrent] = React.useState(0);

    // tylko po to, żeby odpalić preload raz
    const preloadedRef = React.useRef(false);

    React.useEffect(() => {
        if (!api) return;
        setCurrent(api.selectedScrollSnap() + 1);
        api.on("select", () => setCurrent(api.selectedScrollSnap() + 1));
    }, [api]);

    // ✅ preload+decode wszystkich 6 obrazów – zanim user zacznie swipe
    React.useEffect(() => {
        if (preloadedRef.current) return;
        const urls = cards.map((c) => c.imageSrc).filter(Boolean) as string[];
        if (!urls.length) return;

        preloadedRef.current = true;
        void preloadAndDecodeAll(urls);
    }, [cards]);

    if (!cards?.length) return null;

    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="attractions"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "0px", amount: "some" }}
                className={clsx("overflow-hidden pt-16 md:pt-24 lg:pt-28 pb-4 md:pb-6", className)}
            >
                <div className="grid auto-cols-fr grid-cols-1 items-center gap-12 md:gap-16 lg:grid-cols-2 lg:gap-0">
                    {/* LEFT */}
                    <div className="flex lg:justify-self-end">
                        <div className="mx-[5%] w-full max-w-md lg:mb-24 lg:ml-[5vw] lg:mr-28">
                            {tagline ? (
                                <div className="relative mb-10 inline-block md:mb-14 lg:mb-16">
                                    <m.p
                                        variants={slideUp}
                                        custom={0}
                                        className="uppercase whitespace-normal max-w-[10ch] tracking-[0.10em] text-sm"
                                    >
                                        {tagline}
                                    </m.p>
                                    <Underline
                                        delay={0.8}
                                        useWhileInView
                                        className="pointer-events-none absolute -left-2 top-full -mt-1 text-accent h-5 w-[150px] md:h-6 md:w-[180px]"
                                    />
                                </div>
                            ) : null}

                            <m.h2
                                variants={slideUp}
                                custom={1}
                                className="mb-3 text-4xl font-medium md:mb-4 md:text-6xl"
                            >
                                {heading}
                            </m.h2>

                            <m.p variants={slideUp} custom={2} className="md:text-md max-w-sm">
                                {description}
                            </m.p>

                            <m.div
                                variants={slideUp}
                                custom={3}
                                className="pt-8 md:pt-12 flex flex-wrap items-center justify-between gap-4"
                            >
                                {allLink?.href ? (
                                    <span className="inline-flex w-fit" style={{ width: "fit-content" }}>
                    <TextLinkButton
                        text={(allLink.label || "ZOBACZ WSZYSTKIE").toUpperCase()}
                        hoverColor="#803320"
                        href={allLink.href}
                    />
                  </span>
                                ) : null}
                            </m.div>
                        </div>
                    </div>

                    {/* RIGHT */}
                    <Carousel
                        setApi={setApi}
                        opts={{ loop: true, align: "start" }}
                        className="overflow-hidden px-[5%] lg:px-0"
                    >
                        <CarouselContent className="ml-0">
                            {cards.map((card) => {
                                const isEven = card.index % 2 === 0;
                                const bgClass = isEven ? "bg-primary-light" : "bg-secondary";
                                const textClass = isEven ? "text-neutral-black" : "text-primary";

                                return (
                                    <CarouselItem
                                        key={card.key}
                                        className="basis-[95%] pl-0 pr-6 sm:basis-4/5 md:basis-1/2 md:pr-8 lg:basis-4/5"
                                    >
                                        <m.article
                                            variants={slideUp}
                                            custom={2 + card.index}
                                            className={clsx(
                                                "flex h-full flex-col overflow-hidden rounded-[40px] pb-6",
                                                bgClass,
                                                textClass
                                            )}
                                        >
                                            <Link href={card.button.href} scroll className="group block overflow-hidden p-2">
                                                {/* ✅ stabilny wrapper (bez flash/reflow) */}
                                                <div className="relative w-full overflow-hidden rounded-[32px] bg-primary-light">
                                                    <div className="relative aspect-[4/3] w-full">
                                                        {card.imageSrc ? (
                                                            <Image
                                                                src={card.imageSrc}
                                                                alt={card.imageAlt}
                                                                fill
                                                                sizes="(max-width: 768px) 95vw, (max-width: 1024px) 50vw, 40vw"
                                                                // ✅ 6/6 obrazów od razu
                                                                priority
                                                                loading="eager"
                                                                fetchPriority="high"
                                                                // ✅ Sanity CDN: pomijamy optimizer (mniej lagów w karuzeli)
                                                                unoptimized
                                                                decoding="async"
                                                                className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.015]"
                                                            />
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </Link>

                                            <div className="flex flex-1 flex-col justify-between px-5 pt-4 pr-6 md:px-6 md:pt-8 md:pr-7">
                                                <div>
                                                    <h3 className="mb-4 text-md font-medium md:text-2xl uppercase tracking-wide">
                                                        {card.title}
                                                    </h3>
                                                    <p>{card.description}</p>
                                                </div>

                                                <div className="mt-8 self-end mr-4">
                                                    <TextLinkButton
                                                        text={card.button.label}
                                                        hoverColor="#803320"
                                                        primaryColor={isEven ? "#111" : "#FAF6F2"}
                                                        href={card.button.href}
                                                    />
                                                </div>
                                            </div>
                                        </m.article>
                                    </CarouselItem>
                                );
                            })}
                        </CarouselContent>

                        <m.div variants={slideUp} custom={4} className="mt-12 flex items-center justify-end md:justify-between">
                            <div className="flex gap-2 md:gap-4">
                                <CarouselPrevious className="static size-10 rounded-full border bg-primary-light hover:bg-accent hover:text-primary-light transition duration-500 border-primary-light" />
                                <CarouselNext className="static size-10 rounded-full border bg-primary-light border-primary-light hover:bg-accent hover:text-primary-light transition duration-500" />
                            </div>
                        </m.div>
                    </Carousel>
                </div>
            </m.section>
        </LazyMotion>
    );
}